// Imagery bake (OVL-4, M43). PURE + deterministic (GEN-1/ENG-1): aligned imagery — NAIP
// tiles and/or a user overlay — becomes GENERATOR INPUT. A world column's natural ground
// (the grass surface, before roads/water/buildings/aeroway) is repainted to the block
// whose map colour is nearest the imagery pixel over that column, so the same pinned
// bytes yield a byte-identical world. The classifier is a pure function of the imagery
// raster + a serializable palette (built once by the client from the registry — the sim
// generator never touches the registry, so this rides `EarthWorld.imagery` like the DEM).
//
// Only the surface `top` block changes; the crust below, roads, water, buildings, and
// aeroways stay authoritative (vector geometry wins). Absent imagery ⇒ Atlas/Earth
// generation byte-identical to M40 (parity by construction).
import { hexRgb, MATERIAL_MAP_RGB, MAP_FALLBACK } from '../game/cartography';
import type { Registries } from '../registry/load';
import { unprojectBlock } from '../atlas/elevation';
import type { LatLon } from './tiles';

/** A palette entry: a placeable surface block state + its base map RGB (OVL-4 match). */
export interface ImageryPaletteEntry { state: number; r: number; g: number; b: number; }

/** A geo-located imagery raster (a NAIP tile, M43.1): RGBA rows NW→SE over a WGS84 bbox.
 *  `hash` = sha-256 of the source PNG bytes — the provenance + determinism pin (OVL-4). */
export interface EarthImageryTile {
  hash: string;
  south: number; west: number; north: number; east: number;
  width: number; height: number;
  /** width*height*4 bytes, row-major from the NW corner (row 0 = north edge). */
  rgba: Uint8Array;
}

/** A block-located imagery raster (a baked user overlay, M43.2): the aligned ghost plane
 *  frozen into generator input, anchored at its NW corner in block coords. */
export interface EarthImageryOverlay {
  hash: string;
  anchorX: number; anchorZ: number;
  widthBlocks: number; heightBlocks: number;
  width: number; height: number;
  rgba: Uint8Array;
}

/** Everything the generator needs to bake imagery into the surface (rides
 *  `EarthWorld.imagery`; serializable — plain arrays + typed pixels). */
export interface EarthImagery {
  center: LatLon;
  scaleM: number;
  palette: ImageryPaletteEntry[];
  /** NAIP tiles covering the extent (M43.1). */
  tiles: EarthImageryTile[];
  /** Baked user overlays (M43.2); consulted before tiles (a deliberate alignment wins). */
  overlays: EarthImageryOverlay[];
}

/** Curated surface blocks the bake may classify to (OVL-4 "over the block registry",
 *  narrowed to plausible ground materials so a red roof never becomes redstone_block).
 *  Naturals + the 16 concrete colours + terracotta give broad photographic coverage;
 *  any name absent from the registry is skipped. */
export const IMAGERY_PALETTE_BLOCKS: readonly string[] = [
  'grass_block', 'dirt', 'sand', 'gravel', 'stone', 'cobblestone',
  'andesite', 'diorite', 'granite', 'clay', 'snow_block', 'terracotta',
  'white_concrete', 'light_gray_concrete', 'gray_concrete', 'black_concrete',
  'red_concrete', 'orange_concrete', 'yellow_concrete', 'lime_concrete',
  'green_concrete', 'cyan_concrete', 'light_blue_concrete', 'blue_concrete',
  'purple_concrete', 'magenta_concrete', 'pink_concrete', 'brown_concrete',
];

/** Base map RGB of a block (its explicit `mapColor`, else its material colour — the same
 *  source the minimap/map palette uses, UI-8). */
function blockRgb(reg: Registries, name: string): [number, number, number] | null {
  const def = reg.blocks.byName.get(name);
  if (!def) return null;
  return def.mapColor ? hexRgb(def.mapColor) : MATERIAL_MAP_RGB[def.material] ?? MAP_FALLBACK;
}

/** Build the classifier palette from the registry (client-side; the generator receives
 *  the result inside `EarthImagery`). Deterministic — the curated list in registry order. */
export function buildImageryPalette(reg: Registries): ImageryPaletteEntry[] {
  const out: ImageryPaletteEntry[] = [];
  for (const name of IMAGERY_PALETTE_BLOCKS) {
    const state = reg.states.defaultOf.get(name);
    const rgb = blockRgb(reg, name);
    if (state === undefined || !rgb) continue;
    out.push({ state, r: rgb[0], g: rgb[1], b: rgb[2] });
  }
  return out;
}

/** Nearest palette state to an RGB pixel by the "redmean" weighted distance (a cheap
 *  perceptual metric; integer-stable → deterministic). Ties break to the lower index
 *  (palette/registry order), so the classification is total + reproducible. */
export function nearestPaletteState(r: number, g: number, b: number, palette: readonly ImageryPaletteEntry[]): number {
  let best = -1, bestD = Infinity;
  for (const p of palette) {
    const rmean = (p.r + r) / 2;
    const dr = p.r - r, dg = p.g - g, db = p.b - b;
    const d = (2 + rmean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rmean) / 256) * db * db;
    if (d < bestD) { bestD = d; best = p.state; }
  }
  return best;
}

/** Nearest-pixel RGB from a raster's row-major RGBA, or null when the pixel is
 *  transparent (alpha 0 = beyond the source extent — leave the natural ground). */
function samplePixel(rgba: Uint8Array, width: number, height: number, u: number, v: number): [number, number, number] | null {
  const c = Math.min(width - 1, Math.max(0, Math.round(u * (width - 1))));
  const r = Math.min(height - 1, Math.max(0, Math.round(v * (height - 1))));
  const i = (r * width + c) * 4;
  if ((rgba[i + 3] ?? 0) === 0) return null;
  return [rgba[i] ?? 0, rgba[i + 1] ?? 0, rgba[i + 2] ?? 0];
}

/** The imagery colour over a world column [wx,wz] — a baked overlay first (a deliberate
 *  alignment wins where it covers), then the NAIP tiles — or null (no coverage / a
 *  transparent pixel), leaving the natural surface. */
export function sampleImageryRgb(img: EarthImagery, wx: number, wz: number): [number, number, number] | null {
  for (const o of img.overlays) {
    const u = (wx + 0.5 - o.anchorX) / o.widthBlocks;
    const v = (wz + 0.5 - o.anchorZ) / o.heightBlocks;
    if (u < 0 || u >= 1 || v < 0 || v >= 1) continue;
    const rgb = samplePixel(o.rgba, o.width, o.height, u, v);
    if (rgb) return rgb;
  }
  if (img.tiles.length > 0) {
    const { lat, lon } = unprojectBlock(wx, wz, img.center, img.scaleM);
    for (const t of img.tiles) {
      if (lat > t.north || lat < t.south || lon < t.west || lon > t.east) continue;
      const u = (lon - t.west) / (t.east - t.west);
      const v = (t.north - lat) / (t.north - t.south);
      const rgb = samplePixel(t.rgba, t.width, t.height, u, v);
      if (rgb) return rgb;
    }
  }
  return null;
}

/** Surface override for the rasterizer (OVL-4): repaint the natural ground `top` to the
 *  nearest palette block under the imagery, or keep `top` where there's no coverage.
 *  Returns undefined when the world has no imagery ⇒ the generator stays M40-parity. */
export function makeSurfaceOverride(img: EarthImagery | undefined): ((wx: number, wz: number, top: number) => number) | undefined {
  if (!img || img.palette.length === 0 || (img.tiles.length === 0 && img.overlays.length === 0)) return undefined;
  return (wx, wz, top) => {
    const rgb = sampleImageryRgb(img, wx, wz);
    return rgb ? nearestPaletteState(rgb[0], rgb[1], rgb[2], img.palette) : top;
  };
}
